import TableHeaderInfoCard from '@components/V3/TableHeaderInfoCard';
import {
  useGetAllCourses,
  useGetUniversitiesLight,
  useGetUniversityCountriesAndRegions,
} from '@actions';
import { MentorWidgetStatistics } from '@shared/clientTypes';
import AgoraSearchBar from '@utils/AgoraSearchbar';
import { COLORS } from '../../../v3/global';
import './users-table-info.styles.scss';
import Select from '@components/V4/Select/Select';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import { SelectOption, TriState } from '@shared/clientTypes/misc';
import { supportedLanguages } from '@shared/constants';
import TriStateToggle from '@components/V4/TriStateToggle/TriStateToggle';
import { degreeTypes } from '@modules/Account/Profile/components/AccountProfile/constants';
import { useGetAllMajorNames } from 'apps/agora/src/actions/major.actions';
import { useState } from 'react';
import { useDebounce } from '@hooks';

type Props = {
  mentorStatistics?: MentorWidgetStatistics | undefined;
  handleSearch: (searchValue: string) => void;
  search: string;
  handleAddStatus: (statusValue: any) => void;
  handleRemoveStatus: (statusValue: any) => void;
  statuses: string[];
  handleCourse: (course: any) => void;
  handleClearCourse?: () => void;
  course: any;
  handleIsTestUser: (value: TriState) => void;
  handleIsDeactivated: (value: TriState) => void;
  isDeactivated?: boolean;
  isTestUser?: boolean;
  handleExtraFilter: (extraFilter: any) => void;
  extraFilter: any;
  majors: any;
  handleAddMajor: (major: any) => void;
  handleRemoveMajor: (major: any) => void;
  handleClearMajors?: () => void;
  degreeType?: string;
  handleDegreeType?: (degreeType: any) => void;
  handleClearDegreeType?: () => void;
  handleClearStatuses?: () => void;
  isLoadingMentorStatistics: boolean;
  universities?: string[];
  handleAddUniversity: (university: string) => void;
  handleRemoveUniversity: (university: string) => void;
  handleClearUniversity: () => void;
  spokenLanguages?: string[];
  handleSpokenLanguagesChange: (languages: string[]) => void;
  studyLanguages?: string[];
  handleStudyLanguagesChange: (languages: string[]) => void;
  countries?: string[];
  handleCountriesChange: (countries: string[]) => void;
  payRateMin: string;
  payRateMax: string;
  handlePayRateMinChange: (value: string) => void;
  handlePayRateMaxChange: (value: string) => void;
};

const UsersTableInfo = ({
  mentorStatistics,
  handleSearch,
  search,
  handleAddStatus,
  handleRemoveStatus,
  statuses,
  handleCourse,
  handleClearCourse,
  course,
  handleIsTestUser,
  handleIsDeactivated,
  isDeactivated,
  isTestUser,
  handleExtraFilter,
  extraFilter,
  degreeType,
  handleDegreeType,
  handleClearDegreeType,
  handleClearStatuses,
  majors,
  handleAddMajor,
  handleRemoveMajor,
  handleClearMajors,
  isLoadingMentorStatistics,
  universities,
  handleAddUniversity,
  handleRemoveUniversity,
  handleClearUniversity,
  spokenLanguages,
  handleSpokenLanguagesChange,
  studyLanguages,
  handleStudyLanguagesChange,
  countries,
  handleCountriesChange,
  payRateMin,
  payRateMax,
  handlePayRateMinChange,
  handlePayRateMaxChange,
}: Props) => {
  const [universitySearch, setUniversitySearch] = useState('');
  const payRateMinValue = payRateMin === '' ? null : Number(payRateMin);
  const payRateMaxValue = payRateMax === '' ? null : Number(payRateMax);
  const isPayRateRangeInvalid =
    payRateMinValue !== null &&
    payRateMaxValue !== null &&
    !Number.isNaN(payRateMinValue) &&
    !Number.isNaN(payRateMaxValue) &&
    payRateMinValue > payRateMaxValue;

  const debouncedUniversitySearch = useDebounce(universitySearch, 500);

  const { data: universityCountries } = useGetUniversityCountriesAndRegions();
  const { data: allCourses } = useGetAllCourses();
  const { data: allMajors } = useGetAllMajorNames();
  const { data: allUniversities, isLoading: isUniversitiesLoading } = useGetUniversitiesLight({
    search: debouncedUniversitySearch,
    limit: 20,
  });

  const mentorStatuses = [
    { label: 'In Training', value: 'In Training' },
    { label: 'Active', value: 'Active' },
    { label: 'Available', value: 'Available' },
    { label: 'Under Observation', value: 'Under Observation' },
    { label: 'At Capacity', value: 'At Capacity' },
    { label: 'Leaving', value: 'Leaving' },
    { label: 'Inactive', value: 'Inactive' },
  ];

  const getUniversityOptions = () => {
    const universityOptions: SelectOption[] =
      allUniversities?.map((university) => ({
        label: university.name,
        value: university._id,
      })) || [];

    return universityOptions;
  };

  return (
    <div
      style={{
        backgroundColor: COLORS.DARK_GREY_ALT,
        minHeight: '328px',
        padding: '32px 24px',
      }}
    >
      <div
        style={{
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/*HEADER TITLE AND BUTTON*/}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
          }}
        >
          <h1 style={{ color: COLORS.TEXT_PRIMARY }} className="header-title">
            Mentors
          </h1>
          {/*TODO Implement this when add mentor modal is ready*/}
          {/*<SecondaryButton*/}
          {/*  backgroundColor={COLORS.WHITE}*/}
          {/*  height="24px"*/}
          {/*  padding="4px 10px 4px 10px"*/}
          {/*  margin="0px"*/}
          {/*>*/}
          {/*  <PlusOutlined style={{ color: COLORS.BLACK, fontSize: '13px' }} />{' '}*/}
          {/*  <span*/}
          {/*    className="button-text"*/}
          {/*    style={{ marginLeft: '8px', color: COLORS.BLACK }}*/}
          {/*  >*/}
          {/*    Add Mentor*/}
          {/*  </span>*/}
          {/*</SecondaryButton>*/}
        </div>
        <div style={{ maxWidth: '1920px' }}>
          {/*TABLE HEADER INFO*/}

          {!isLoadingMentorStatistics ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                marginBottom: '16px',
              }}
            >
              <TableHeaderInfoCard
                title={mentorStatistics?.activeMentors?.count ?? 'N/A'}
                description={'Active'}
                statuses={statuses}
                status={'Active'}
                handleExtraFilter={handleExtraFilter}
                handleAddStatus={handleAddStatus}
                handleRemoveStatus={handleRemoveStatus}
                handleClearStatuses={handleClearStatuses}
              />
              <TableHeaderInfoCard
                title={mentorStatistics?.maxCapacity?.count ?? 'N/A'}
                description={'At max. capacity'}
                statuses={statuses}
                status={'At Capacity'}
                handleExtraFilter={handleExtraFilter}
                handleAddStatus={handleAddStatus}
                handleRemoveStatus={handleRemoveStatus}
                handleClearStatuses={handleClearStatuses}
              />
              <TableHeaderInfoCard
                title={mentorStatistics?.inTraining?.count ?? 'N/A'}
                description={'In training'}
                statuses={statuses}
                status={'In Training'}
                handleExtraFilter={handleExtraFilter}
                handleAddStatus={handleAddStatus}
                handleRemoveStatus={handleRemoveStatus}
                handleClearStatuses={handleClearStatuses}
              />
              <TableHeaderInfoCard
                title={mentorStatistics?.noSessionsHeld?.count ?? 'N/A'}
                description={'Not started'}
                handleExtraFilter={handleExtraFilter}
                extraFilter={extraFilter}
                handleClearStatuses={handleClearStatuses}
              />
              <TableHeaderInfoCard
                title={mentorStatistics?.noRecentSessions?.count ?? 'N/A'}
                description={'No recent session'}
                handleExtraFilter={handleExtraFilter}
                extraFilter={extraFilter}
                handleClearStatuses={handleClearStatuses}
              />
              <TableHeaderInfoCard
                title={mentorStatistics?.missingFeedback?.count ?? 'N/A'}
                description={'Missing feedback'}
                handleExtraFilter={handleExtraFilter}
                extraFilter={extraFilter}
                handleClearStatuses={handleClearStatuses}
              />
              <TableHeaderInfoCard
                title={mentorStatistics?.nextSessionMissing?.count ?? 'N/A'}
                description={'Next session missing'}
                margin="0 16px 8px 0px"
                handleExtraFilter={handleExtraFilter}
                extraFilter={extraFilter}
                handleClearStatuses={handleClearStatuses}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center py-10">
              <LoadingSpinner />
            </div>
          )}
          {/*SEARCHBAR*/}
          <AgoraSearchBar
            search={search}
            handleSearch={handleSearch}
            placeholder={'Search by name, e-mail, phone, universities...'}
          />

          <div className="flex flex-wrap gap-4">
            {/*STATUSES*/}
            <Select
              className="max-w-64"
              variant="secondary"
              label="Statuses"
              onSelect={handleAddStatus}
              onDeselect={handleRemoveStatus}
              onClear={handleClearStatuses}
              options={mentorStatuses}
              mode="multiple"
            />

            {/*COURSE*/}
            <Select
              className="max-w-64"
              variant="secondary"
              label="Course"
              onSelect={handleCourse}
              options={allCourses?.map((course) => ({
                value: course._id,
                label: course.title,
              }))}
              onClear={handleClearCourse}
            />

            {/*MAJOR*/}
            <Select
              className="max-w-64"
              variant="secondary"
              label="Majors"
              mode="multiple"
              value={majors}
              onSelect={handleAddMajor}
              onDeselect={handleRemoveMajor}
              onClear={handleClearMajors}
              options={allMajors?.map(({ _id, name }) => ({
                value: name,
                label: name,
              }))}
            />

            {/*DEGREE TYPE*/}
            <Select
              className="max-w-64"
              variant="secondary"
              label="Degree Type"
              onSelect={handleDegreeType}
              options={degreeTypes.map((degreeType: string) => ({
                value: degreeType,
                label: degreeType,
              }))}
              onClear={handleClearDegreeType}
            />

            {/*UNIVERSITY*/}

            <Select
              className="w-1/6 max-w-56"
              label="University"
              value={universities}
              filterOption={() => true}
              searchValue={universitySearch}
              onSearch={setUniversitySearch}
              onBlur={() => setUniversitySearch('')}
              onSelect={(value) => handleAddUniversity(value as string)}
              onDeselect={(value) => handleRemoveUniversity(value as string)}
              onClear={() => handleClearUniversity()}
              options={getUniversityOptions()}
              notFoundContent={
                isUniversitiesLoading ? <LoadingSpinner className="mx-auto" size="normal" /> : null
              }
              placeholder="Search Universities"
              variant="secondary"
              mode="multiple"
            />

            {/*SPOKEN LANGUAGES*/}

            <Select
              className="w-1/6 max-w-56"
              label="Spoken languages"
              value={spokenLanguages}
              onChange={(value) => handleSpokenLanguagesChange((value as string[]) ?? [])}
              options={supportedLanguages.map((language) => ({
                value: language,
                label: language,
              }))}
              placeholder="Any language"
              variant="secondary"
              mode="multiple"
              allowClear
            />

            {/*STUDY LANGUAGE*/}

            <Select
              className="w-1/6 max-w-56"
              label="Studied in"
              value={studyLanguages}
              onChange={(value) => handleStudyLanguagesChange((value as string[]) ?? [])}
              options={supportedLanguages.map((language) => ({
                value: language,
                label: language,
              }))}
              placeholder="Any language"
              variant="secondary"
              mode="multiple"
              allowClear
            />

            {/*UNIVERSITY COUNTRY*/}

            <Select
              className="w-1/6 max-w-56"
              label="University country"
              value={countries}
              onChange={(value) => handleCountriesChange((value as string[]) ?? [])}
              options={(universityCountries?.countries ?? []).map((country: string) => ({
                value: country,
                label: country,
              }))}
              placeholder="Any country"
              variant="secondary"
              mode="multiple"
              showSearch
              allowClear
            />

            <div className="flex flex-col">
              <label className="text-sm text-white mb-2">Pay Rate (EUR)</label>
              <div className="flex items-center gap-2">
                <input
                  className="mentors-row-custom-select-component"
                  style={{ width: '96px' }}
                  inputMode="decimal"
                  placeholder="From"
                  value={payRateMin}
                  onChange={(event) => {
                    const value = event.target.value;
                    if (value === '' || /^\d*\.?\d*$/.test(value)) {
                      handlePayRateMinChange(value);
                    }
                  }}
                />
                <span className="text-white">-</span>
                <input
                  className="mentors-row-custom-select-component"
                  style={{ width: '96px' }}
                  inputMode="decimal"
                  placeholder="To"
                  value={payRateMax}
                  onChange={(event) => {
                    const value = event.target.value;
                    if (value === '' || /^\d*\.?\d*$/.test(value)) {
                      handlePayRateMaxChange(value);
                    }
                  }}
                />
              </div>
              {isPayRateRangeInvalid && (
                <span className="text-xs text-red-400 mt-1">
                  Min must be less than or equal to max.
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-6 mt-6">
            <TriStateToggle
              title="Show Test Users:"
              options={[
                { label: 'Regular', value: 'unchecked' },
                { label: 'Test', value: 'checked' },
                { label: 'All', value: 'indeterminate' },
              ]}
              value={isTestUser}
              onChange={handleIsTestUser}
            />
            <TriStateToggle
              title="Active Status:"
              options={[
                { label: 'Active', value: 'unchecked' },
                { label: 'Deactivated', value: 'checked' },
                { label: 'All', value: 'indeterminate' },
              ]}
              value={isDeactivated}
              onChange={handleIsDeactivated}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsersTableInfo;
